"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ACCOUNT_HEADS_MAX = exports.SERVICE_CODES_MAX = exports.SERVICE_CODE_PATTERN = exports.CONCESSION_TYPE_CODE_PATTERN = exports.CONSULTATION_HEAD_NAME = exports.RULE_SCOPE_CODE = exports.RULE_SCOPE_GROUPS = exports.RULE_SCOPE_HELP = exports.RULE_SCOPE_LABEL = exports.RULE_FAMILIES = exports.RULE_SCOPES = void 0;
exports.isConsultationHeadName = isConsultationHeadName;
exports.normalizeServiceCodes = normalizeServiceCodes;
exports.scopeNeedsServiceCodes = scopeNeedsServiceCodes;
exports.scopeNeedsAccountHeads = scopeNeedsAccountHeads;
exports.scopeOfRule = scopeOfRule;
exports.storedFieldsFor = storedFieldsFor;
exports.describeRuleScope = describeRuleScope;
exports.broaderScopesFor = broaderScopesFor;
exports.isInvestigationScope = isInvestigationScope;
exports.concessionCategoryLabel = concessionCategoryLabel;
exports.isBuiltInConcessionCategory = isBuiltInConcessionCategory;
/**
 * WHAT A CONCESSION RULE COVERS - the "Applies to" choice and the words for it, in ONE place so the rule form, the rules
 * table and the billing engine read the same thing.
 *
 * Owner, 2026-10-17: "Services Type: Service Code, Lab, Radiology, Lab + Radiology, Service + Lab, All. Appointment Type:
 * which head starts from CONS. Account Head: bulk selection of account head and individual selection, typed and searchable";
 * then "appointment is applied to CONS FEE - OP is correct"; "only option to consultation fees to avail discount 100 flat";
 * "old concession rules should be matched here"; "service code also should avail 30% discount to staff - presently lab and
 * radiology is considering".
 *
 * A rule is for ONE kind of thing - the form first asks which:
 *
 *   SERVICES        what is billed
 *     SERVICE_CODE            the services you list, by code (`serviceCodes`)
 *     LAB                     every laboratory test                      (`serviceFamily = 'LAB'`)
 *     RADIOLOGY               every radiology service                    (`serviceFamily = 'RADIOLOGY'`)
 *     LAB_RADIOLOGY           lab AND radiology, one rate for both
 *     SERVICE_LAB             the listed services AND every lab test     (`serviceCodes` + `serviceFamily = 'LAB'`)
 *     SERVICE_RADIOLOGY       the listed services AND every radiology service
 *     SERVICE_LAB_RADIOLOGY   the listed services AND lab AND radiology
 *     OTHER                   every other SERVICE CODE - not lab, radiology or the consultation (the rules written before this: STAFF-OP-100).
 *                             A line with no service code (billed straight to an account head, not CONS FEE - OP) is covered by NO family rule.
 *     ALL                     every line of the bill
 *   APPOINTMENT     the consultation fee: every account head named CONS FEE - OP (one per department), and the Consultation Fee
 *                   dialog's own line. NOT CONS FEE - IP (inpatient) and not CONSULTATION FE: a "starts with CONS" test would
 *                   have caught both - 21 + 1 more heads and 115 inpatient services - so the name is matched in full.
 *   ACCOUNT_HEAD    the account heads you pick (`accountHeadIds`) - one, several, or all that match a search; a service filed
 *                   under one of those heads is covered, and so is a line billed straight to the head.
 *
 * WHICH RULE WINS FOR ONE LINE - the most specific, whatever the priority numbers say:
 *   1. a rule naming THIS service code      2. a rule for the line's account head   3. a rule for the line's own family
 *   4. (older) the single-head rule         5. Lab + Radiology (investigation lines)  6. All
 * Inside one step the order that already held stays: priority, then a rule naming the category over the catch-all, then the
 * newest. A rule at rate 0 is an exclusion and ends the search at its step. A "Service Code + Lab" rule is both a step-1 rule
 * (for its codes) and a step-3 rule (for lab), which is how it covers the union.
 *
 * Pure by construction (no Prisma, no clock) - the browser and the server both import it.
 */
const concession_policy_1 = require("./concession-policy");
exports.RULE_SCOPES = [
    'SERVICE_CODE',
    'LAB',
    'RADIOLOGY',
    'LAB_RADIOLOGY',
    'SERVICE_LAB',
    'SERVICE_RADIOLOGY',
    'SERVICE_LAB_RADIOLOGY',
    'OTHER',
    'ALL',
    'APPOINTMENT',
    'ACCOUNT_HEAD',
];
exports.RULE_FAMILIES = ['LAB', 'RADIOLOGY', 'LAB_RADIOLOGY', 'APPOINTMENT', 'OTHER', 'ALL'];
exports.RULE_SCOPE_LABEL = {
    SERVICE_CODE: 'Service Code',
    LAB: 'Lab',
    RADIOLOGY: 'Radiology',
    LAB_RADIOLOGY: 'Lab + Radiology',
    SERVICE_LAB: 'Service Code + Lab',
    SERVICE_RADIOLOGY: 'Service Code + Radiology',
    SERVICE_LAB_RADIOLOGY: 'Service Code + Lab + Radiology',
    OTHER: 'Other OP services',
    ALL: 'All',
    APPOINTMENT: 'Consultation OP (CONS FEE - OP)',
    ACCOUNT_HEAD: 'Account head',
};
/** One line under the dropdown: what the choice means at the counter. */
exports.RULE_SCOPE_HELP = {
    SERVICE_CODE: 'Only the services whose codes you list - for example one procedure, or a short list of tests.',
    LAB: 'Every laboratory test.',
    RADIOLOGY: 'Every radiology service (X-ray, scan and the rest).',
    LAB_RADIOLOGY: 'Every laboratory test and every radiology service - one rate for both investigations.',
    SERVICE_LAB: 'The services you list, and every laboratory test.',
    SERVICE_RADIOLOGY: 'The services you list, and every radiology service.',
    SERVICE_LAB_RADIOLOGY: 'The services you list, and every laboratory test, and every radiology service.',
    OTHER: 'Every other service code - any service that is not lab, radiology or the consultation. A line with no service code (billed straight to an account head) is not covered.',
    ALL: 'Every line on the bill - consultation, lab, radiology and every other service.',
    APPOINTMENT: 'Only the consultation fee - the account head CONS FEE - OP (one per department): the Consultation Fee dialog on the Appointment screen, and that head at the Cash Counter. Nothing else on the bill.',
    ACCOUNT_HEAD: 'Every service and line filed under the account heads you pick.',
};
/** The form's first choice, and what it holds. */
exports.RULE_SCOPE_GROUPS = [
    {
        key: 'SERVICES',
        label: 'Services',
        help: 'By what is billed: particular services, lab, radiology, or everything.',
        scopes: ['SERVICE_CODE', 'LAB', 'RADIOLOGY', 'LAB_RADIOLOGY', 'SERVICE_LAB', 'SERVICE_RADIOLOGY', 'SERVICE_LAB_RADIOLOGY', 'OTHER', 'ALL'],
    },
    { key: 'APPOINTMENT', label: 'Consultation OP', help: 'Only the consultation fee - the account head CONS FEE - OP.', scopes: ['APPOINTMENT'] },
    { key: 'ACCOUNT_HEAD', label: 'Account head', help: 'By the account head a service or line is filed under.', scopes: ['ACCOUNT_HEAD'] },
];
/** Short piece of a generated rule code: `CONC-SVC-STF-001`. */
exports.RULE_SCOPE_CODE = {
    SERVICE_CODE: 'SVC',
    LAB: 'LAB',
    RADIOLOGY: 'RAD',
    LAB_RADIOLOGY: 'LABRAD',
    SERVICE_LAB: 'SVCLAB',
    SERVICE_RADIOLOGY: 'SVCRAD',
    SERVICE_LAB_RADIOLOGY: 'SVCLR',
    OTHER: 'OTH',
    ALL: 'ALL',
    APPOINTMENT: 'APPT',
    ACCOUNT_HEAD: 'HEAD',
};
/** The outpatient consultation account head (owner: "appointment is applied to CONS FEE - OP"). There is one per department; the NAME is what they share. */
exports.CONSULTATION_HEAD_NAME = 'CONS FEE - OP';
/** Whitespace-tolerant, case-insensitive, and the WHOLE name: CONS FEE - IP and CONSULTATION FE are different heads. */
function isConsultationHeadName(name) {
    return (name ?? '').trim().replace(/\s+/g, ' ').toUpperCase() === exports.CONSULTATION_HEAD_NAME;
}
/** The code of a concession type an administrator adds: upper-case letters, digits and underscore, starting with a letter. The built-in five fit it too. */
exports.CONCESSION_TYPE_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,19}$/;
/** A service code is the charge code the Service Master shows (ANA1, XRT12, FEE5) - letters and digits, as typed in the catalogue. */
exports.SERVICE_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]{0,29}$/;
exports.SERVICE_CODES_MAX = 200;
exports.ACCOUNT_HEADS_MAX = 500;
/** Typed or pasted codes -> clean list: split on commas / spaces / lines, upper-case, trimmed, no blanks, no repeats. */
function normalizeServiceCodes(raw) {
    const pieces = Array.isArray(raw) ? raw : String(raw ?? '').split(/[\s,;]+/);
    const seen = new Set();
    for (const piece of pieces) {
        const code = String(piece).trim().toUpperCase();
        if (code)
            seen.add(code);
    }
    return [...seen];
}
/** Does this scope need service codes / account heads to be named? */
function scopeNeedsServiceCodes(scope) {
    return scope === 'SERVICE_CODE' || scope === 'SERVICE_LAB' || scope === 'SERVICE_RADIOLOGY' || scope === 'SERVICE_LAB_RADIOLOGY';
}
function scopeNeedsAccountHeads(scope) {
    return scope === 'ACCOUNT_HEAD';
}
const FAMILY_OF_SERVICE_UNION = {
    SERVICE_LAB: 'LAB',
    SERVICE_RADIOLOGY: 'RADIOLOGY',
    SERVICE_LAB_RADIOLOGY: 'LAB_RADIOLOGY',
};
/** The choice a stored rule stands for - what the form shows when it is opened to update. null = the single-account-head rule of the older style, or nothing recognisable. */
function scopeOfRule(rule) {
    if (rule.accountHeadIds && rule.accountHeadIds.length > 0)
        return 'ACCOUNT_HEAD';
    if (rule.serviceCodes && rule.serviceCodes.length > 0) {
        switch (rule.serviceFamily) {
            case null:
                return 'SERVICE_CODE';
            case 'LAB':
                return 'SERVICE_LAB';
            case 'RADIOLOGY':
                return 'SERVICE_RADIOLOGY';
            case 'LAB_RADIOLOGY':
                return 'SERVICE_LAB_RADIOLOGY';
            default:
                return null;
        }
    }
    return exports.RULE_FAMILIES.includes(rule.serviceFamily ?? '') ? rule.serviceFamily : null;
}
/** What to store for a chosen scope. */
function storedFieldsFor(scope, serviceCodes, accountHeadIds = []) {
    if (scope === 'ACCOUNT_HEAD')
        return { serviceFamily: null, serviceCodes: [], accountHeadIds: [...new Set(accountHeadIds)] };
    if (scope === 'SERVICE_CODE')
        return { serviceFamily: null, serviceCodes: normalizeServiceCodes(serviceCodes), accountHeadIds: [] };
    const union = FAMILY_OF_SERVICE_UNION[scope];
    if (union)
        return { serviceFamily: union, serviceCodes: normalizeServiceCodes(serviceCodes), accountHeadIds: [] };
    return { serviceFamily: scope, serviceCodes: [], accountHeadIds: [] };
}
/** The words for a stored rule's scope - the rules table. `headNames` (by id) lets an account-head rule read as its heads. */
function describeRuleScope(rule, headNames) {
    const scope = scopeOfRule(rule);
    const codes = rule.serviceCodes ?? [];
    const codesText = codes.length <= 3 ? codes.join(', ') : `${codes.slice(0, 2).join(', ')} +${codes.length - 2} more`;
    if (scope === 'ACCOUNT_HEAD') {
        const names = (rule.accountHeadIds ?? []).map((id) => headNames?.[id] ?? 'head');
        return names.length <= 2 ? `Head ${names.join(', ')}` : `${names.length} account heads (${names.slice(0, 2).join(', ')} ...)`;
    }
    if (scope === 'SERVICE_CODE')
        return `Service ${codesText}`;
    if (scope === 'SERVICE_LAB' || scope === 'SERVICE_RADIOLOGY' || scope === 'SERVICE_LAB_RADIOLOGY') {
        return `${exports.RULE_SCOPE_LABEL[scope]} (${codesText})`;
    }
    return scope ? exports.RULE_SCOPE_LABEL[scope] : 'Account head only';
}
/** The broader scopes that ALSO cover a line of this family, narrowest first. A Lab line is covered by Lab + Radiology and All; a consultation only by All. */
function broaderScopesFor(family) {
    return family === 'LAB' || family === 'RADIOLOGY' ? ['LAB_RADIOLOGY', 'ALL'] : ['ALL'];
}
/**
 * Lab, Radiology or both. (Retired staff rules used to have to be one of these - an investigation concession. Since the owner's
 * confirmed rates table, 2026-10-17, a Retired rule may be written for ANY scope; this stays as a helper for the words only.)
 */
function isInvestigationScope(scope) {
    return scope === 'LAB' || scope === 'RADIOLOGY' || scope === 'LAB_RADIOLOGY';
}
/** The category words shown anywhere a rule or a counter names who a concession is for: the built-in five, or an administrator-added type. */
function concessionCategoryLabel(code, custom) {
    if (code === null)
        return 'All categories';
    return concession_policy_1.CONCESSION_CATEGORY_LABEL[code] ?? custom?.[code] ?? code;
}
/** Built-in category? (the five fixed in the system; every other code is an administrator-added concession type) */
function isBuiltInConcessionCategory(code) {
    return Object.prototype.hasOwnProperty.call(concession_policy_1.CONCESSION_CATEGORY_LABEL, code);
}
//# sourceMappingURL=discount-rule-scope.js.map